// Computes dial layout dimensions and ring view models for library host customization

import { useMemo } from 'react';
import type { UserPreferences } from '@airhead-games/shared/types';
import type { LibraryPoppyDialOption } from './useLibraryPoppyDialOptions';

export type LibraryPoppyDialRing = {
  id: string;
  radius: number;
  thickness: number;
  options: LibraryPoppyDialOption[];
  selectedId: string;
  hoveredId: string | null;
  onSelect: (optionId: string) => void;
};

interface UseLibraryPoppyDialLayoutArgs {
  windowWidth: number;
  userPreferences: UserPreferences;
  hoveredRing: Record<string, string | null>;
  personalityOptions: LibraryPoppyDialOption[];
  accentOptions: LibraryPoppyDialOption[];
  genderOptions: LibraryPoppyDialOption[];
  onPersonalitySelect: (optionId: string) => void;
  onAccentSelect: (optionId: string) => void;
  onGenderSelect: (optionId: string) => void;
}

interface UseLibraryPoppyDialLayoutResult {
  dialSize: number;
  poppySize: number;
  center: number;
  rings: LibraryPoppyDialRing[];
}

export function useLibraryPoppyDialLayout({
  windowWidth,
  userPreferences,
  hoveredRing,
  personalityOptions,
  accentOptions,
  genderOptions,
  onPersonalitySelect,
  onAccentSelect,
  onGenderSelect,
}: UseLibraryPoppyDialLayoutArgs): UseLibraryPoppyDialLayoutResult {
  const dialSize = Math.max(320, Math.min(520, Math.round(windowWidth * 0.96)));
  const poppySize = Math.round(
    Math.max(160, Math.min(240, dialSize * (240 / 520)))
  );
  const center = dialSize / 2;
  const outerThickness = dialSize * 0.11;
  const middleThickness = dialSize * 0.095;
  const innerThickness = dialSize * 0.085;
  const ringGap = 0;
  const outerInset = dialSize * 0.03;
  const outerRadius = center - outerThickness / 2 - outerInset;
  const middleRadius =
    outerRadius - outerThickness / 2 - ringGap - middleThickness / 2;
  const innerRadius =
    middleRadius - middleThickness / 2 - ringGap - innerThickness / 2;

  const rings = useMemo<LibraryPoppyDialRing[]>(
    () => [
      {
        id: 'personality',
        radius: outerRadius,
        thickness: outerThickness,
        options: personalityOptions,
        selectedId: userPreferences.hostStyle.personality,
        hoveredId: hoveredRing.personality,
        onSelect: onPersonalitySelect,
      },
      {
        id: 'accent',
        radius: middleRadius,
        thickness: middleThickness,
        options: accentOptions,
        selectedId: userPreferences.accent,
        hoveredId: hoveredRing.accent,
        onSelect: onAccentSelect,
      },
      {
        id: 'gender',
        radius: innerRadius,
        thickness: innerThickness,
        options: genderOptions,
        selectedId: userPreferences.hostStyle.gender,
        hoveredId: hoveredRing.gender,
        onSelect: onGenderSelect,
      },
    ],
    [
      accentOptions,
      genderOptions,
      hoveredRing.accent,
      hoveredRing.gender,
      hoveredRing.personality,
      innerRadius,
      innerThickness,
      middleRadius,
      middleThickness,
      onAccentSelect,
      onGenderSelect,
      onPersonalitySelect,
      outerRadius,
      outerThickness,
      personalityOptions,
      userPreferences.accent,
      userPreferences.hostStyle.gender,
      userPreferences.hostStyle.personality,
    ]
  );

  return { dialSize, poppySize, center, rings };
}
